// Print.js

const PRINT_FRAME_ATTRIBUTE = 'data-nodal-print-frame';

const DESKTOP_PRINT_CLEANUP_DELAY_MS = 60 * 1000;
const ANDROID_PRINT_CLEANUP_DELAY_MS = 10 * 60 * 1000;

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const isAndroidPrintEnvironment = () => {
  if (typeof navigator === 'undefined') return false;
  return /android/i.test(String(navigator.userAgent || ''));
};

const schedulePrintFrameCleanup = (iframe, delayMs) => {
  if (!iframe || typeof window === 'undefined') return;

  window.setTimeout(() => {
    if (iframe.parentNode) {
      iframe.parentNode.removeChild(iframe);
    }
  }, delayMs);
};

const removeExistingPrintFrames = () => {
  if (typeof document === 'undefined') return;

  document.querySelectorAll(`[${PRINT_FRAME_ATTRIBUTE}="true"]`).forEach((frame) => {
    if (frame.parentNode) {
      frame.parentNode.removeChild(frame);
    }
  });
};

export const openPrintDocument = ({
  bodyMarkup = '',
  onPrintTriggered,
  styles = '',
  title = 'Print Preview',
} = {}) => {
  if (typeof document === 'undefined' || typeof window === 'undefined') return false;

  removeExistingPrintFrames();

  const iframe = document.createElement('iframe');
  iframe.setAttribute('title', String(title || 'Print Preview'));
  iframe.setAttribute(PRINT_FRAME_ATTRIBUTE, 'true');
  iframe.style.border = '0';
  iframe.style.height = '0';
  iframe.style.opacity = '0';
  iframe.style.pointerEvents = 'none';
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';

  document.body.appendChild(iframe);

  const printDocument = iframe.contentWindow?.document;
  const printWindow = iframe.contentWindow;

  if (!printDocument || !printWindow) {
    if (iframe.parentNode) {
      iframe.parentNode.removeChild(iframe);
    }
    return false;
  }

  printDocument.open();
  printDocument.write(`
    <!DOCTYPE html>
    <html lang="en">
      <head>
        <meta charset="utf-8" />
        <title>${escapeHtml(title)}</title>
        <style>${styles}</style>
      </head>
      <body>
        ${bodyMarkup}
      </body>
    </html>
  `);
  printDocument.close();

  let didPrint = false;

  const cleanupDelayMs = isAndroidPrintEnvironment()
    ? ANDROID_PRINT_CLEANUP_DELAY_MS
    : DESKTOP_PRINT_CLEANUP_DELAY_MS;

  const triggerPrint = () => {
    if (didPrint) return;
    didPrint = true;

    printWindow.focus();

    try {
      printWindow.print();
    } finally {
      if (typeof onPrintTriggered === 'function') {
        onPrintTriggered();
      }
    }

    // Keep the backing document alive after preview/dialog launch. Android print
    // services may still resolve the final job from this document after preview.
    schedulePrintFrameCleanup(iframe, cleanupDelayMs);
  };

  iframe.onload = () => {
    window.setTimeout(triggerPrint, 50);
  };

  window.setTimeout(triggerPrint, 250);
  return true;
};